













import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import { AdcontrolProfile, AgentCompanyAssociationDuplicate } from "@/models";
import { Getter, Action } from "vuex-class";
import ClientCompany from "@/models/Agents/Company";
import ClientAgentProduct, {
  MissingPrincipalProductRecord,
} from "@/models/Agents/Product";
import MatchProblemGroup from "@/models/Agents/MatchProblemGroup";
import AgentsMatchProblemGroupMixin, {
  AgentsMatchProblemGroupMixinTags,
} from "@/mixins/http/Agents/AgentsMatchProblemGroupMixin";
import Company, { AgentsCompanyMixinTags } from "@/mixins/http/Agents/Company";
import Product, { AgentProductMixinTags } from "@/mixins/http/Agents/Product";
import ToastInvoker from "@/utils/ToastInvoker";
import { RequestData } from "@/store/types/RequestStore";
import AgentCompanyAssociationDuplicatesMixin, {
  AgentCompanyAssociationDuplicateMixinTags,
} from "@/mixins/http/Agents/AgentCompanyAssociationDuplicateMixin";

const API_CALL_TAGS = {
  Company: AgentsCompanyMixinTags.GetCompanies,
  Products: AgentProductMixinTags.GetProductsWithoutPrincipals,
  MultipleProducts: AgentProductMixinTags.GetMultipleProductsGroupsNames,
  MatchProblem: AgentsMatchProblemGroupMixinTags.GetMatchProblems,
  CompanyAssociationDuplicates:
    AgentCompanyAssociationDuplicateMixinTags.GetDuplicates,
} as const;

const enum NotificationBadgeTags {
  Company = "get_companies_notifications",
  Products = "get_products_notifications",
  MultipleProducts = "get_multiple_products_notifications",
  MatchProblem = "get_match_problems_notifications",
  CompanyAssociationDuplicates = "get_company_association_duplicates_notifications",
}

export enum NotificationBadgeGroups {
  Company = "companies",
  Products = "products",
  MultipleProducts = "multiple_products",
  MatchProblem = "match_problems",
  CompanyAssociationDuplicates = "company_association_duplicates",
}
const NOTIFICATION_RELOAD_INTERVAL = 30_000; // 30 seconds

@Component
export default class NotificationBadge extends Mixins(
  Company,
  Product,
  AgentsMatchProblemGroupMixin,
  AgentCompanyAssociationDuplicatesMixin
) {
  @Prop() readonly notificationBadgeGroups!: NotificationBadgeGroups[];
  @Prop({ default: "md" }) readonly size?: string;
  @Prop({ default: "sidebar" }) readonly kind?: string;

  @Getter("profile") readonly profile!: AdcontrolProfile;
  @Getter("notificationsGroups/reload")
  readonly reloadNotificationsState!: Record<NotificationBadgeGroups, boolean>;
  @Getter("notificationsGroups/company")
  readonly companyNotifications!: number;
  @Getter("notificationsGroups/products")
  readonly productsNotifications!: number;
  @Getter("notificationsGroups/multipleProducts")
  readonly multipleProductsNotifications!: number;
  @Getter("notificationsGroups/matchProblems")
  readonly matchProblemsNotifications!: number;
  @Getter("notificationsGroups/companyAssociationDuplicates")
  readonly companyAssociationDuplicatesNotifications!: number;
  @Getter("http/status") readonly httpStatus!: { [key: string]: RequestData };

  @Action("notificationsGroups/stopReload")
  readonly stopReload!: (group: NotificationBadgeGroups) => void;
  @Action("notificationsGroups/valorizeCompany")
  readonly valorizeCompany!: (notificationCount: number) => void;
  @Action("notificationsGroups/valorizeProducts")
  readonly valorizeProducts!: (notificationCount: number) => void;
  @Action("notificationsGroups/valorizeMultipleProducts")
  readonly valorizeMultipleProducts!: (notificationCount: number) => void;
  @Action("notificationsGroups/valorizeMatchProblems")
  readonly valorizeMatchProblems!: (notificationCount: number) => void;
  @Action("notificationsGroups/valorizeCompanyAssociationDuplicates")
  readonly valorizeCompanyAssociationDuplicates!: (
    notificationCount: number
  ) => void;

  @Watch("reloadNotificationsState", { deep: true })
  onReloadChanged(newVal: Record<NotificationBadgeGroups, boolean>) {
    for (const [key, value] of Object.entries(newVal) as [
      NotificationBadgeGroups,
      boolean
    ][]) {
      if (value) {
        this.handleReload(key);
      }
    }
  }

  // INSTANCE PROPERTIES
  private _isDestroyed = false;
  notificationInterval: number | undefined;

  // LIFECYCLE EVENTS

  mounted() {
    this.$nextTick(() => this.init());
  }

  beforeDestroy() {
    this._isDestroyed = true;

    if (this.notificationInterval) {
      clearInterval(this.notificationInterval);
      this.notificationInterval = undefined;
    }
  }

  // GETTERS

  get loading(): boolean {
    return (
      (this.hasGroup(NotificationBadgeGroups.Company) && this.companyLoading) ||
      (this.hasGroup(NotificationBadgeGroups.Products) &&
        this.productsLoading) ||
      (this.hasGroup(NotificationBadgeGroups.MultipleProducts) &&
        this.multipleProductsLoading) ||
      (this.hasGroup(NotificationBadgeGroups.MatchProblem) &&
        this.matchProblemLoading) ||
      (this.hasGroup(NotificationBadgeGroups.CompanyAssociationDuplicates) &&
        this.companyAssociationDuplicatesLoading)
    );
  }

  get companyLoading(): boolean {
    return (
      (this.httpStatus[API_CALL_TAGS.Company]?.loading ?? false) ||
      (this.httpStatus[NotificationBadgeTags.Company]?.loading ?? false)
    );
  }

  get productsLoading(): boolean {
    return (
      (this.httpStatus[API_CALL_TAGS.Products]?.loading ?? false) ||
      (this.httpStatus[NotificationBadgeTags.Products]?.loading ?? false)
    );
  }

  get multipleProductsLoading(): boolean {
    return (
      (this.httpStatus[API_CALL_TAGS.MultipleProducts]?.loading ?? false) ||
      (this.httpStatus[NotificationBadgeTags.MultipleProducts]?.loading ??
        false)
    );
  }

  get matchProblemLoading(): boolean {
    return (
      (this.httpStatus[API_CALL_TAGS.MatchProblem]?.loading ?? false) ||
      (this.httpStatus[NotificationBadgeTags.MatchProblem]?.loading ?? false)
    );
  }

  get companyAssociationDuplicatesLoading(): boolean {
    return (
      (this.httpStatus[API_CALL_TAGS.CompanyAssociationDuplicates]?.loading ??
        false) ||
      (this.httpStatus[NotificationBadgeTags.CompanyAssociationDuplicates]
        ?.loading ??
        false)
    );
  }

  get notificationsCount(): number {
    let total = 0;

    if (this.hasGroup(NotificationBadgeGroups.Company)) {
      total += this.companyNotifications;
    }

    if (this.hasGroup(NotificationBadgeGroups.Products)) {
      total += this.productsNotifications;
    }

    if (this.hasGroup(NotificationBadgeGroups.MultipleProducts)) {
      total += this.multipleProductsNotifications;
    }

    if (this.hasGroup(NotificationBadgeGroups.MatchProblem)) {
      total += this.matchProblemsNotifications;
    }

    if (this.hasGroup(NotificationBadgeGroups.CompanyAssociationDuplicates)) {
      total += this.companyAssociationDuplicatesNotifications;
    }

    return total;
  }

  get kindStyle() {
    if (this.kind === "sidebar") return "sidebar-position";
    if (this.kind === "multiswitcher") return "multiswitcher-position";
    return "title-position";
  }

  get sizeStyle() {
    if (this.size === "md") return "size-md";
    if (this.size === "sm") return "size-sm";
    return "size-lg";
  }

  // ASYNC METHODS

  private async safeCall<T>(promise: Promise<T>): Promise<T | null> {
    try {
      const result = await promise;
      if (this._isDestroyed) return null;
      return result;
    } catch (err) {
      if (this._isDestroyed) return null;
      throw err;
    }
  }

  private runIfAlive(fn: () => void) {
    if (!this._isDestroyed) fn();
  }

  async valorizeNotificationsByGroup(
    group: NotificationBadgeGroups
  ): Promise<void> {
    try {
      let count: number | undefined;
      let valorizeFn: ((n: number) => void) | undefined;

      if (group === NotificationBadgeGroups.Company) {
        count = await this.getCompanyWithoutPrincipalCount();
        valorizeFn = this.valorizeCompany;
      } else if (group === NotificationBadgeGroups.Products) {
        count = await this.getProductsWithoutPrincipalCount();
        valorizeFn = this.valorizeProducts;
      } else if (group === NotificationBadgeGroups.MultipleProducts) {
        count = await this.getMultipleProductsCount();
        valorizeFn = this.valorizeMultipleProducts;
      } else if (group === NotificationBadgeGroups.MatchProblem) {
        count = await this.getProductsWithConflictsCount();
        valorizeFn = this.valorizeMatchProblems;
      } else if (
        group === NotificationBadgeGroups.CompanyAssociationDuplicates
      ) {
        count = await this.getCompanyAssociationDuplicatesCount();
        valorizeFn = this.valorizeCompanyAssociationDuplicates;
      }

      if (!valorizeFn || count === undefined) return;

      const safeCount = count;
      const safeFn = valorizeFn;

      this.runIfAlive(() => safeFn(safeCount));
    } catch (error) {
      this.runIfAlive(() =>
        ToastInvoker.errorToast(
          `Errore nel caricamento delle notifiche per il gruppo: ${group}`,
          error
        )
      );
    }
  }

  async getCompanyWithoutPrincipalCount(): Promise<number> {
    const companies: ClientCompany[] | null = await this.safeCall(
      this.getCompanies(NotificationBadgeTags.Company, this.profile.slug, {
        "filter[with_no_principal]": 1,
        "page[size]": "all",
      })
    );

    if (!companies) return 0;

    const notifications = [
      ...new Set(companies.map((c: ClientCompany) => c.name)),
    ];
    return notifications.length;
  }

  async getProductsWithoutPrincipalCount(): Promise<number> {
    const productsWithoutPrincipals: ClientAgentProduct[] | null =
      await this.safeCall(
        this.getProductsWithoutPrincipals(
          NotificationBadgeTags.Products,
          this.profile.slug,
          {
            "page[size]": "all",
            include: "*.principal_agent_company",
          }
        )
      );

    if (!productsWithoutPrincipals) return 0;

    const companyMap = new Set<string>();

    for (const product of productsWithoutPrincipals) {
      const companyId: string | undefined = product.principalAgentCompany?.id;
      if (companyId && !companyMap.has(companyId)) {
        companyMap.add(companyId);
      }
    }

    return companyMap.size;
  }

  async getMultipleProductsCount(): Promise<number> {
    const multipleProductsGroupNames: string[] | null = await this.safeCall(
      this.getMultipleProductsGroupsNames(
        NotificationBadgeTags.MultipleProducts,
        this.profile.slug
      )
    );

    if (!multipleProductsGroupNames) return 0;

    const notifications: string[] = multipleProductsGroupNames;

    return notifications.length;
  }

  async getProductsWithConflictsCount(): Promise<number> {
    const matchProblemGroups: MatchProblemGroup[] | null = await this.safeCall(
      this.getMatchProblemsGroups(
        NotificationBadgeTags.MatchProblem,
        this.profile.slug
      )
    );

    if (!matchProblemGroups) return 0;

    return matchProblemGroups.length;
  }

  async getCompanyAssociationDuplicatesCount(): Promise<number> {
    const companyAssociationDuplicates:
      | AgentCompanyAssociationDuplicate[]
      | null = await this.safeCall(
      this.getDuplicates(
        this.profile.slug,
        NotificationBadgeTags.CompanyAssociationDuplicates,
        { include: "agent_company,agency,agency_agent,agent_product" }
      )
    );

    if (!companyAssociationDuplicates) return 0;

    const generateDuplicateKey = (
      duplicate: AgentCompanyAssociationDuplicate
    ) =>
      `${duplicate.agentCompanyId}/${duplicate.agentProductId ?? "no_product"}`;

    const duplicateSets = new Map<string, AgentCompanyAssociationDuplicate[]>();
    const keySet = new Set<string>();

    for (const duplicate of companyAssociationDuplicates) {
      const key = generateDuplicateKey(duplicate);
      keySet.add(key);

      if (!duplicateSets.has(key)) {
        duplicateSets.set(key, []);
      }

      duplicateSets.get(key)!.push(duplicate);
    }

    for (const set of duplicateSets.values()) {
      set.sort((a, b) => a.mediaId - b.mediaId);
    }

    return duplicateSets.size;
  }

  // SYNC METHODS

  init(): void {
    if (!this.notificationBadgeGroups.length) return;

    if (!this.loading) this.checkNotifications();

    if (!this.notificationInterval) {
      this.notificationInterval = window.setInterval(() => {
        if (!this.loading) this.checkNotifications();
      }, NOTIFICATION_RELOAD_INTERVAL);
    }
  }

  handleReload(key: NotificationBadgeGroups) {
    this.valorizeNotificationsByGroup(key);
    this.stopReload(key);
  }

  checkNotifications() {
    if (this.profile) {
      for (const group of this.notificationBadgeGroups) {
        this.valorizeNotificationsByGroup(group);
      }
    }
  }

  // UTILS METHODS

  hasGroup(group: NotificationBadgeGroups): boolean {
    return group ? this.notificationBadgeGroups.includes(group) : false;
  }
}
